import React, { useEffect, useState } from 'react';
import DatePicker from '../components/DatePicker';
import { reportsAPI } from '../services/api';
import { formatDate, getTodayKualaLumpurISODate, toKualaLumpurISODate } from '../utils/dateFormat';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/ReportPages.module.css';

const downloadCsv = (filename, rows) => {
  const escapeCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const ResourceReportPage = () => {
  const { t } = useLocalization();
  const [mode, setMode] = useState('monthly');
  const [reportType, setReportType] = useState('summary');
  const [groupBy, setGroupBy] = useState('resource');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return toKualaLumpurISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return toKualaLumpurISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (nextMode === 'daily') {
      const day = getTodayKualaLumpurISODate();
      setStartDate(day);
      setEndDate(day);
    } else if (nextMode === 'monthly') {
      setStartDate(toKualaLumpurISODate(new Date(year, month, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, month + 1, 0)));
    } else if (nextMode === 'yearly') {
      setStartDate(toKualaLumpurISODate(new Date(year, 0, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, 11, 31)));
    }
    // range keeps current dates
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await reportsAPI.resourceActivityReport({ mode, startDate, endDate, groupBy });
      setRows(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || t('resource_report_load_failed', 'Failed to load resource activity report'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // Initial load only; report refresh is user-triggered via View button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = () => {
    if (reportType === 'summary') {
      const csvRows = [
        [
          groupBy === 'type' ? 'Resource Type' : 'Resource',
          t('resource_report_type', 'Type'),
          t('dashboard_total_tasks', 'Total Tasks'),
          t('resource_report_quantity_used', 'Quantity Used'),
          t('tasks_status_completed', 'Completed'),
          t('tasks_status_in_progress', 'In Progress'),
          t('tasks_status_pending', 'Pending'),
          t('tasks_status_cancelled', 'Cancelled'),
          t('user_report_completion_pct', 'Completion %'),
        ],
        ...rows.map((row) => {
          const pct = row.total_tasks > 0 ? Math.round((Number(row.completed) / Number(row.total_tasks)) * 100) : 0;
          return [
            row.group_label,
            row.resource_type || '-',
            row.total_tasks,
            row.total_quantity_used,
            row.completed,
            row.in_progress,
            row.pending,
            row.cancelled,
            `${pct}%`,
          ];
        }),
      ];
      downloadCsv(t('resource_report_summary_export_filename', 'resource-activity-summary.csv'), csvRows);
      return;
    }

    const csvRows = [[
      groupBy === 'type' ? 'Resource Type' : 'Resource',
      t('task_report_task_date', 'Task Date'),
      t('resource_report_task_title', 'Task Title'),
      t('tasks_label_team', 'Team'),
      t('tasks_label_status', 'Status'),
      t('tasks_label_priority', 'Priority'),
      t('resource_report_quantity_used', 'Quantity Used'),
    ]];

    rows.forEach((group) => {
      if (!Array.isArray(group.tasks) || group.tasks.length === 0) {
        csvRows.push([group.group_label, '-', '-', '-', '-', '-', '-']);
      } else {
        group.tasks.forEach((task) => {
          csvRows.push([
            group.group_label,
            task.task_date,
            task.title,
            task.team_name || '-',
            task.status,
            task.priority,
            task.quantity_used,
          ]);
        });
      }
    });

    downloadCsv(t('resource_report_detail_export_filename', 'resource-activity-detail.csv'), csvRows);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('resource_report_title', 'Resource Activity Report')}</h1>
        <p>{t('resource_report_subtitle', 'Track resource involvement in tasks by individual resource or by resource type.')}</p>
      </div>

      <div className={styles.controls}>
        <div className={styles.modeButtons}>
          {['daily', 'monthly', 'yearly', 'range'].map((item) => (
            <button
              key={item}
              type="button"
              className={`${styles.modeBtn} ${mode === item ? styles.modeBtnActive : ''}`}
              onClick={() => handleModeChange(item)}
            >
              {item === 'daily'
                ? t('common_daily', 'Daily')
                : item === 'monthly'
                  ? t('common_monthly', 'Monthly')
                  : item === 'yearly'
                    ? t('common_yearly', 'Yearly')
                    : t('common_range', 'Range')}
            </button>
          ))}
        </div>

        <select className={styles.select} value={reportType} onChange={(e) => setReportType(e.target.value)}>
          <option value="summary">{t('reports_summary', 'Summary')}</option>
          <option value="detail">{t('reports_detail', 'Detail')}</option>
        </select>

        <select className={styles.select} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          <option value="resource">{t('resource_report_group_by_resource', 'Group by Resource')}</option>
          <option value="type">{t('resource_report_group_by_type', 'Group by Resource Type')}</option>
        </select>

        <DatePicker
          value={startDate}
          onChange={mode === 'range' ? setStartDate : undefined}
          placeholder={t('tasks_start_date', 'Start date')}
          className={styles.dateInput}
          disabled={mode !== 'range'}
        />
        <DatePicker
          value={endDate}
          onChange={mode === 'range' ? setEndDate : undefined}
          placeholder={t('tasks_end_date', 'End date')}
          className={styles.dateInput}
          disabled={mode !== 'range'}
        />

        <button className={styles.actionBtn} type="button" onClick={fetchReport}>{t('reports_view', 'View')}</button>
        <button className={styles.actionBtnSecondary} type="button" onClick={handleExport} disabled={rows.length === 0}>{t('reports_export_excel', 'Export Excel')}</button>
      </div>

      {loading ? (
        <p>{t('resource_report_loading', 'Loading resource activity report...')}</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <>
          {reportType === 'summary' ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{groupBy === 'type' ? t('resource_report_resource_type', 'Resource Type') : t('resource_report_resource', 'Resource')}</th>
                    <th>{t('resource_report_type', 'Type')}</th>
                    <th>{t('dashboard_total_tasks', 'Total Tasks')}</th>
                    <th>{t('resource_report_quantity_used', 'Quantity Used')}</th>
                    <th>{t('tasks_status_completed', 'Completed')}</th>
                    <th>{t('tasks_status_in_progress', 'In Progress')}</th>
                    <th>{t('tasks_status_pending', 'Pending')}</th>
                    <th>{t('tasks_status_cancelled', 'Cancelled')}</th>
                    <th>{t('user_report_completion_pct', 'Completion %')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={styles.empty}>{t('resource_report_no_data', 'No resource activity data found.')}</td>
                    </tr>
                  ) : rows.map((row) => {
                    const total = Number(row.total_tasks || 0);
                    const pct = total > 0 ? Math.round((Number(row.completed) / total) * 100) : 0;
                    return (
                      <tr key={`${row.group_label}-${row.resource_type}`}>
                        <td><strong>{row.group_label}</strong></td>
                        <td>{row.resource_type || '-'}</td>
                        <td><strong>{total}</strong></td>
                        <td>{row.total_quantity_used}</td>
                        <td><span className={`${styles.statusBadge} ${styles.status_completed}`}>{row.completed}</span></td>
                        <td><span className={`${styles.statusBadge} ${styles.status_in_progress}`}>{row.in_progress}</span></td>
                        <td><span className={`${styles.statusBadge} ${styles.status_pending}`}>{row.pending}</span></td>
                        <td><span className={`${styles.statusBadge} ${styles.status_cancelled}`}>{row.cancelled}</span></td>
                        <td>
                          <div className={styles.pctBar}>
                            <div className={styles.pctFill} style={{ width: `${pct}%` }} />
                            <span className={styles.pctLabel}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.detailCardsWrap}>
              {rows.length === 0 ? (
                <p className={styles.empty}>{t('resource_report_no_data', 'No resource activity data found.')}</p>
              ) : rows.map((group) => (
                <div key={`${group.group_label}-${group.resource_type}`} className={styles.detailCard}>
                  <div className={styles.detailCardHeader}>
                    <span className={styles.detailCardTitle}>{group.group_label}</span>
                    <div className={styles.detailCardBadges}>
                      <span className={styles.teamsBadge}>{group.resource_type || '-'}</span>
                      <span className={styles.teamsBadge}>{t('resource_report_qty_used_short', 'Qty Used')}: {group.total_quantity_used}</span>
                    </div>
                  </div>

                  <div className={styles.detailCardBody}>
                    <div className={styles.detailCardGrid}>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('dashboard_total_tasks', 'Total Tasks')}</span>
                        <span>{group.total_tasks}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_status_completed', 'Completed')}</span>
                        <span>{group.completed}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_status_in_progress', 'In Progress')}</span>
                        <span>{group.in_progress}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_status_pending', 'Pending')}</span>
                        <span>{group.pending}</span>
                      </div>
                    </div>

                    {Array.isArray(group.tasks) && group.tasks.length > 0 ? (
                      <div className={styles.userTaskList}>
                        <div className={styles.userTaskListHeader}>{t('user_report_tasks_in_period', 'Tasks in period')}</div>
                        {group.tasks.map((task) => (
                          <div key={`${group.group_label}-${task.task_id}-${task.quantity_used}`} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_date', 'Date')}</span>
                              <span className={styles.userTaskValue}>{formatDate(task.task_date)}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_task', 'Task')}</span>
                              <span className={`${styles.userTaskValue} ${styles.userTaskTitle}`}>{task.title}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_team', 'Team')}</span>
                              <span className={styles.userTaskValue}>{task.team_name || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_status', 'Status')}</span>
                              <span className={`${styles.statusBadge} ${styles[`status_${task.status}`]}`}>
                                {task.status?.replace('_', ' ')}
                              </span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('resource_report_quantity', 'Quantity')}</span>
                              <span className={styles.userTaskValue}>{task.quantity_used}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.userNoTasks}>{t('user_report_no_tasks_period', 'No tasks assigned in this period.')}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ResourceReportPage;
